import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Save, Upload, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function CompanySettings() {
  const [form, setForm] = useState({
    company_name: "", address: "", gst_number: "", mobile: "",
    email: "", website: "", signature_path: "", voucher_logo_path: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, sig: false });

  useEffect(() => {
    api.get("/settings/company").then((r) => {
      setForm({
        company_name: r.data.company_name || "",
        address: r.data.address || "",
        gst_number: r.data.gst_number || "",
        mobile: r.data.mobile || "",
        email: r.data.email || "",
        website: r.data.website || "",
        signature_path: r.data.signature_path || "",
        voucher_logo_path: r.data.voucher_logo_path || "",
      });
      setLoading(false);
    });
  }, []);

  const upload = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading((u) => ({ ...u, [field === "voucher_logo_path" ? "logo" : "sig"]: true }));
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.path }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [field === "voucher_logo_path" ? "logo" : "sig"]: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/company", form);
      toast.success("Company details saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500">Loading…</div>;

  return (
    <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="company-settings-form">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Administration</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Company Settings</h1>
          <p className="text-zinc-500 mt-2 text-sm">These details are used in Job Sheets, Invoices, and Statement PDFs across the ERP.</p>
        </div>
        <Button type="submit" disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 h-10" data-testid="save-company-button">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-zinc-500" />
          <h3 className="font-heading font-bold text-lg tracking-tight">Business Details</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2 sm:col-span-2">
            <Label>Company Name *</Label>
            <Input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="company-name-input" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Address</Label>
            <Textarea rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="company-address-input" placeholder="Full address (line breaks allowed)" />
          </div>
          <div className="space-y-2">
            <Label>GST Number</Label>
            <Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} data-testid="company-gst-input" placeholder="24ABCDE1234F1Z5" />
          </div>
          <div className="space-y-2">
            <Label>Mobile</Label>
            <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="company-mobile-input" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Website</Label>
            <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg tracking-tight">Invoice & PDF Logo</h3>
        <p className="text-xs text-zinc-500">Uploaded logo will appear in Job Sheets, Invoices, and Statements. If left empty, the default logo is used.</p>
        <div className="flex items-start gap-4">
          <div className="w-32 h-24 rounded-md bg-zinc-100 overflow-hidden border border-border grid place-items-center">
            {form.voucher_logo_path ? (
              <img src={fileUrl(form.voucher_logo_path)} alt="" className="w-full h-full object-contain" />
            ) : (
              <span className="text-xs text-zinc-400">No logo</span>
            )}
          </div>
          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-border rounded-md cursor-pointer text-sm hover:bg-zinc-50">
              <Upload className="w-3.5 h-3.5" />
              {uploading.logo ? "Uploading…" : (form.voucher_logo_path ? "Replace Logo" : "Upload Logo")}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload("voucher_logo_path", e)} data-testid="company-logo-input" />
            </label>
            {form.voucher_logo_path && (
              <button type="button" className="flex items-center gap-1 text-xs text-red-600 hover:underline" onClick={() => setForm({ ...form, voucher_logo_path: "" })}>
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg tracking-tight">Authorised Signature (optional)</h3>
        <p className="text-xs text-zinc-500">Upload a transparent PNG signature to embed on PDF invoices and Job Sheets.</p>
        <div className="flex items-start gap-4">
          <div className="w-40 h-16 rounded-md bg-zinc-100 overflow-hidden border border-border grid place-items-center">
            {form.signature_path ? (
              <img src={fileUrl(form.signature_path)} alt="" className="w-full h-full object-contain" />
            ) : (
              <span className="text-xs text-zinc-400">No signature</span>
            )}
          </div>
          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-border rounded-md cursor-pointer text-sm hover:bg-zinc-50">
              <Upload className="w-3.5 h-3.5" />
              {uploading.sig ? "Uploading…" : (form.signature_path ? "Replace" : "Upload")}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload("signature_path", e)} />
            </label>
            {form.signature_path && (
              <button type="button" className="flex items-center gap-1 text-xs text-red-600 hover:underline" onClick={() => setForm({ ...form, signature_path: "" })}>
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
        </div>
      </Card>
    </form>
  );
}
